import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Phone,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  Mail,
  Send
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Schedule Assessment
 * 
 * Step 2 of the Intake Workflow
 * Schedule a home visit assessment for the service request
 */

const TIME_SLOTS = [
  { value: '08:00', label: '8:00 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
];

export default function ScheduleAssessment() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [request, setRequest] = useState(null);
  
  const [assessmentDate, setAssessmentDate] = useState('');
  const [assessmentTime, setAssessmentTime] = useState('');
  const [assessorName, setAssessorName] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchRequest();
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/service-requests/${requestId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setRequest(data);
        // Pre-fill with existing scheduled date if any
        if (data.assessment_scheduled_date) {
          setAssessmentDate(data.assessment_scheduled_date);
        }
        if (data.assessment_scheduled_time) {
          setAssessmentTime(data.assessment_scheduled_time);
        }
      } else {
        toast.error('Service request not found');
        navigate('/organization/data-entry');
      }
    } catch (err) {
      console.error('Error fetching request:', err);
      toast.error('Failed to load service request');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async () => {
    if (!assessmentDate) {
      toast.error('Please select an assessment date');
      return;
    }

    setSaving(true);
    try {
      // Schedule the assessment
      const params = new URLSearchParams({
        assessment_date: assessmentDate,
      });
      if (assessmentTime) {
        params.append('assessment_time', assessmentTime);
      }

      const res = await fetch(`${API_URL}/api/intake/service-requests/${requestId}/schedule-assessment?${params}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (res.ok) {
        toast.success('Assessment scheduled successfully!');
        
        // Optionally send notification
        if (sendNotification && request?.client_phone) {
          // In a real app, this would trigger an email/SMS notification
          toast.info('Notification will be sent to the client');
        }

        navigate('/organization/data-entry');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to schedule assessment');
      }
    } catch (err) {
      console.error('Error scheduling assessment:', err);
      toast.error('Failed to schedule assessment');
    } finally {
      setSaving(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176DFF]" />
      </div>
    );
  }

  if (!request) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={() => navigate('/organization/data-entry')}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to CareHub
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#0D1B33]">Schedule Assessment</h1>
              <p className="text-sm text-[#64748B]">Step 2 of 6 — Home Visit Scheduling</p>
            </div>
          </div>
        </header>

        {/* Client Info Summary */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
            <h2 className="font-semibold text-[#0D1B33]">Client Information</h2>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-[#64748B]" />
              <span className="font-medium text-[#0D1B33]">{request.client_name}</span>
            </div>
            {request.client_phone && (
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#64748B]" />
                <span className="text-[#64748B]">{request.client_phone}</span>
              </div>
            )}
            {request.client_address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#64748B] mt-0.5" />
                <span className="text-[#64748B]">{request.client_address}</span>
              </div>
            )}
            {request.representative_name && (
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-[#64748B]" />
                <span className="text-[#64748B]">
                  Contact: {request.representative_name}
                  {request.representative_relationship && ` (${request.representative_relationship})`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Schedule Form */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
            <h2 className="font-semibold text-[#0D1B33]">Assessment Details</h2>
          </div>
          <div className="p-5 space-y-5">
            {/* Date Selection */}
            <div>
              <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                Assessment Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  type="date"
                  value={assessmentDate}
                  onChange={(e) => setAssessmentDate(e.target.value)}
                  min={today}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Time Selection */}
            <div>
              <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                Preferred Time
              </label>
              <div className="grid grid-cols-5 gap-2">
                {TIME_SLOTS.map((slot) => (
                  <button
                    key={slot.value}
                    onClick={() => setAssessmentTime(slot.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      assessmentTime === slot.value
                        ? 'bg-[#176DFF] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {slot.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Assessor */}
            <div>
              <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                Assessor Name
              </label>
              <Input
                value={assessorName}
                onChange={(e) => setAssessorName(e.target.value)}
                placeholder="Who will conduct the assessment?"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions for the assessment visit..."
                className="w-full px-4 py-2 border border-[#E2E8F0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF]"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Notification Option */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
          <div className="p-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-[#CBD5E1] text-[#176DFF] focus:ring-[#176DFF]"
              />
              <div>
                <span className="text-sm font-medium text-[#0D1B33] flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Send confirmation email
                </span>
                <p className="text-xs text-[#64748B] mt-1">
                  An email notification will be sent to the client/representative confirming the assessment appointment.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Summary Preview */}
        {assessmentDate && (
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-900">Assessment Scheduled</p>
                <p className="text-sm text-emerald-700 mt-1">
                  Home visit assessment for <strong>{request.client_name}</strong> on{' '}
                  <strong>{new Date(assessmentDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                  {assessmentTime && ` at ${TIME_SLOTS.find(s => s.value === assessmentTime)?.label || assessmentTime}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => navigate('/organization/data-entry')}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Cancel
          </Button>
          <Button
            onClick={handleSchedule}
            disabled={!assessmentDate || saving}
            className="bg-[#176DFF] hover:bg-[#176DFF]/90"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Scheduling...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Schedule Assessment
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
